import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
  Table,
  Tag,
  Checkbox,
  Badge,
  Divider,
  Space,
} from "antd";
import moment from "moment";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import ReactToPrint from "react-to-print";
import {
  PrinterOutlined,
  PlusOutlined,
  DeleteOutlined,
  DownOutlined,
  RightOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { API } from "../../Config";
import { GET, POST, PUT } from "../../Config/ApiRequests";
import "./style.css";

const { RangePicker } = DatePicker;

const FormModalReceipt = ({
  visible,
  onClose,
  mode = "post",
  item,
  page_mode,
  columnsVisible,
  accounts = [],
  defultcurrency,
  onchange,
}) => {
  const { id: userId } = useSelector((state) => state.Auth.user);
  const [form] = Form.useForm();
  const printRef = useRef();

  const [loading, setLoading] = useState(false);
  const [salesData, setSalesData] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(
    item?.voucherDetails?.[0]?.account_no
  );
  const [selectedSupplier, setSelectedSupplier] = useState(item?.account_no);
  const [receiptType, setReceiptType] = useState(() => {
    if (mode === "put") {
      if (item?.voucherDetails?.[0]?.sales_id) return "sales";
      return "other";
    }
    return "sales";
  });
  const [selectedItems, setSelectedItems] = useState({});
  const [paymentAmounts, setPaymentAmounts] = useState({});
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [showNewItems, setShowNewItems] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [otherItems, setOtherItems] = useState(() => {
    if (mode === "put" && receiptType === "other" && item?.voucherDetails) {
      return item.voucherDetails.map((d, i) => ({
        id: d.key || i + 1,
        account_no: d.account_no || "",
        description: d.description || "",
        amount: d.amount_cr?.toString() || "",
      }));
    }
    return [{ id: 1, account_no: "", description: "", amount: "" }];
  });

  const isSales = receiptType === "sales";
  const isOther = receiptType === "other";
  const voucherKeys = useMemo(
    () => (item?.voucherDetails || []).map((d) => String(d.key)),
    [item?.voucherDetails]
  );

  // Get max allowed amount for an item (outstanding amount)
  const getMaxAllowed = useCallback(
    (child) => {
      if (!child) return 0;
      const total = Number(child.total || 0);
      const paid = Number(child.paid_amount || 0);
      let max = total - paid;
      // In edit mode, add back the amount already paid in this voucher
      if (mode === "put") {
        const vd = item?.voucherDetails?.find(
          (v) => String(v.key) === String(child.id)
        );
        max += Number(vd?.amount_cr) || 0;
      }
      return max;
    },
    [mode, item?.voucherDetails]
  );

  // Transform API data - FILTER OUT FULLY PAID + DATE & SEARCH FILTERS
  const parentData = useMemo(() => {
    if (!salesData?.data || isOther) return [];

    const source = salesData.data;
    if (!source || !Array.isArray(source)) return [];

    return source
      .map((sale) => {
        // Date filter - check sale date
        const saleDate =
          sale.sale_date || sale.created_at || sale.createdAt;

        if (dateRange && dateRange[0] && dateRange[1] && saleDate) {
          const itemDate = moment(saleDate);
          if (itemDate.isValid()) {
            const startDate = moment(dateRange[0]).startOf("day");
            const endDate = moment(dateRange[1]).endOf("day");
            if (!itemDate.isBetween(startDate, endDate, null, "[]")) {
              return null;
            }
          }
        }

        const rawChildren = sale.saleDetails || [];
        // Use sale description as the name, fallback to account name or ID
        const saleName = sale.description || sale.chartOfAccount?.account_name || `Sale #${sale.id}`;
        // Map children with additional info
        const allChildren = rawChildren.map((detail) => ({
          ...detail,
          sales_id: sale.id,
          account_no: selectedAccount,
          parentName: saleName,
          saleDate: saleDate,
          // Flag to identify existing voucher items in edit mode
          isExistingItem:
            mode === "put" && voucherKeys.includes(String(detail.id)),
        }));

        // Filter children based on mode and showNewItems toggle
        const children = allChildren.filter((c) => {
          const total = Number(c.total || 0);
          const paid = Number(c.paid_amount || 0);
          let max = total - paid;
          if (mode === "put") {
            const vd = item?.voucherDetails?.find(
              (v) => String(v.key) === String(c.id)
            );
            max += Number(vd?.amount_cr) || 0;
          }
          // In edit mode without showNewItems, only show existing items
          if (mode === "put" && !showNewItems && !c.isExistingItem) {
            return false;
          }
          return max > 0;
        });

        if (children.length === 0) return null;

        const totalAmt = children.reduce(
          (s, c) => s + Number(c.total || 0),
          0
        );
        const totalPaid = children.reduce(
          (s, c) => s + Number(c.paid_amount || 0),
          0
        );

        const name = saleName;
        const subtext = sale.chartOfAccount?.account_name || "";

        // Search filter
        if (searchText) {
          const searchLower = searchText.toLowerCase();
          const searchMatch =
            name.toLowerCase().includes(searchLower) ||
            subtext.toLowerCase().includes(searchLower) ||
            children.some(
              (c) =>
                (c.name || "").toLowerCase().includes(searchLower) ||
                (c.description || "").toLowerCase().includes(searchLower)
            );
          if (!searchMatch) return null;
        }

        return {
          key: `sale-${sale.id}`,
          id: sale.id,
          name,
          subtext,
          date: saleDate,
          totalAmt,
          totalPaid,
          remaining: totalAmt - totalPaid,
          items: children,
        };
      })
      .filter(Boolean);
  }, [
    salesData,
    isOther,
    mode,
    voucherKeys,
    item?.voucherDetails,
    dateRange,
    searchText,
    showNewItems,
    selectedAccount,
  ]);

  // Get selected items data for the bottom table
  const selectedItemsData = useMemo(() => {
    const result = [];
    parentData.forEach((p) =>
      (p.items || []).forEach((c) => {
        if (selectedItems[c.id]) {
          result.push({ ...c, parentName: p.name });
        }
      })
    );
    return result;
  }, [parentData, selectedItems]);

  // Initialize edit mode - select existing voucher items
  useEffect(() => {
    if (mode !== "put" || !parentData.length || !item?.voucherDetails) return;

    const sel = {},
      amt = {},
      exp = [];
    parentData.forEach((p) => {
      let hasSelected = false;
      (p.items || []).forEach((c) => {
        const vd = item.voucherDetails.find(
          (v) => String(v.key) === String(c.id)
        );
        if (vd) {
          sel[c.id] = true;
          amt[c.id] = Number(vd.amount_cr) || 0;
          hasSelected = true;
        }
      });
      if (hasSelected) exp.push(p.key);
    });

    setSelectedItems((prev) => ({ ...prev, ...sel }));
    setPaymentAmounts((prev) => ({ ...prev, ...amt }));
    setExpandedKeys((prev) => [...new Set([...prev, ...exp])]);
  }, [mode, parentData, item?.voucherDetails]);

  // Reset on type change
  useEffect(() => {
    setSelectedItems({});
    setPaymentAmounts({});
    setExpandedKeys([]);
    setDateRange(null);
    setSearchText("");
    setShowNewItems(false);
    if (isOther && mode !== "put") {
      setOtherItems([{ id: 1, account_no: "", description: "", amount: "" }]);
    }
  }, [receiptType, mode, isOther]);

  // Fetch sales data when account is selected (for create mode)
  useEffect(() => {
    if (!selectedSupplier || isOther || mode === "put") return;
    setTableLoading(true);
    GET(`${API.SALE}/byAccount/${selectedSupplier}`)
      .then((res) => {
        setSalesData(res);
        setTableLoading(false);
      })
      .catch((error) => {
        message.error(error.message);
        setTableLoading(false);
      });
  }, [selectedSupplier, isOther, mode]);

  // Fetch existing voucher sales in edit mode
  useEffect(() => {
    if (mode !== "put" || !isSales || !item?.voucherDetails) return;

    const salesIds = [
      ...new Set(
        item.voucherDetails.map((d) => d.sales_id).filter(Boolean)
      ),
    ];

    if (salesIds.length === 0) return;

    setTableLoading(true);

    const fetchExistingSales = async () => {
      try {
        // Fetch existing voucher sales using byIds API
        const idsParam = salesIds.join(",");
        const response = await GET(`${API.SALE}/byIds?ids=${idsParam}`);
        const existingSales = response?.data || [];

        // If showNewItems is enabled, also fetch pending sales from account
        if (showNewItems && selectedSupplier) {
          const pendingResponse = await GET(`${API.SALE}/byAccount/${selectedSupplier}`);
          const pendingSales = pendingResponse?.data || [];

          // Merge existing and pending sales (avoid duplicates)
          const existingIds = new Set(existingSales.map((s) => s.id));
          const mergedSales = [
            ...existingSales,
            ...pendingSales.filter((s) => !existingIds.has(s.id)),
          ];

          setSalesData({ data: mergedSales });
        } else {
          setSalesData({ data: existingSales });
        }

        setTableLoading(false);
      } catch (error) {
        message.error(error.message);
        setTableLoading(false);
      }
    };

    fetchExistingSales();
  }, [mode, isSales, item?.voucherDetails, showNewItems, selectedSupplier]);

  // Handle item selection
  const onSelectItem = useCallback(
    (id, checked, child) => {
      setSelectedItems((prev) => ({ ...prev, [id]: checked }));
      if (checked && !paymentAmounts[id]) {
        const max = getMaxAllowed(child);
        setPaymentAmounts((prev) => ({ ...prev, [id]: max > 0 ? max : 0 }));
      }
    },
    [paymentAmounts, getMaxAllowed]
  );

  // Handle payment change
  const onPaymentChange = useCallback(
    (id, value, child) => {
      const max = getMaxAllowed(child);
      const val = Math.min(Math.max(parseFloat(value) || 0, 0), max);
      setPaymentAmounts((prev) => ({ ...prev, [id]: val }));
    },
    [getMaxAllowed]
  );

  // Select all in parent
  const onSelectAllInParent = useCallback(
    (parent, checked) => {
      const updates = {},
        amtUpdates = {};
      (parent?.items || []).forEach((c) => {
        const max = getMaxAllowed(c);
        if (max > 0) {
          updates[c.id] = checked;
          if (checked && !paymentAmounts[c.id]) amtUpdates[c.id] = max;
        }
      });
      setSelectedItems((prev) => ({ ...prev, ...updates }));
      if (checked) setPaymentAmounts((prev) => ({ ...prev, ...amtUpdates }));
    },
    [getMaxAllowed, paymentAmounts]
  );

  // Remove from selected
  const onRemoveSelected = useCallback((id) => {
    setSelectedItems((prev) => ({ ...prev, [id]: false }));
  }, []);

  // Calculate total
  const total = useMemo(
    () =>
      Object.entries(selectedItems).reduce(
        (sum, [id, sel]) =>
          sel ? sum + (Number(paymentAmounts[id]) || 0) : sum,
        0
      ),
    [selectedItems, paymentAmounts]
  );

  // Helper to get payment status
  const getPaymentStatus = useCallback((paid, total) => {
    if (paid >= total) return { text: "Paid", color: "green" };
    if (paid > 0) return { text: "Partial", color: "orange" };
    return { text: "Unpaid", color: "red" };
  }, []);

  // Parent columns
  const parentCols = useMemo(
    () => [
      {
        title: "Sale",
        key: "name",
        render: (_, r) => (
          <div>
            <strong>{r?.name}</strong>
            {r?.subtext && (
              <div style={{ fontSize: 12, color: "#666" }}>{r.subtext}</div>
            )}
          </div>
        ),
      },
      {
        title: "Date",
        dataIndex: "date",
        width: 100,
        align: "center",
        render: (v) => (v ? moment(v).format("DD/MM/YYYY") : "-"),
      },
      {
        title: "Items",
        dataIndex: "items",
        width: 70,
        align: "center",
        render: (c) => (
          <Badge count={c?.length || 0} showZero color="#1890ff" />
        ),
      },
      {
        title: "Total",
        dataIndex: "totalAmt",
        width: 100,
          align: "right",
        render: (v) => (v || 0).toFixed(2),
      },
      {
        title: "Paid",
        dataIndex: "totalPaid",
        width: 100,
          align: "right",
        render: (v) => (v || 0).toFixed(2),
      },
      {
        title: "Remaining",
        dataIndex: "remaining",
        width: 100,
          align: "right",
        render: (v) => (
          <span style={{ color: "#f5222d" }}>{(v || 0).toFixed(2)}</span>
        ),
      },
      {
        title: "Payment",
        width: 80,
          align: "center",
        render: (_, r) => {
          const status = getPaymentStatus(
            r?.totalPaid || 0,
            r?.totalAmt || 0
          );
          return <Tag color={status.color}>{status.text}</Tag>;
        },
      },
      {
        title: "Selected",
        width: 90,
        align: "center",
        render: (_, r) => {
          const count = (r?.items || []).filter(
            (c) => selectedItems[c?.id]
          ).length;
          if (count > 0) return <Tag color="blue">{count} Selected</Tag>;
          return <span style={{ color: "#999" }}>-</span>;
        },
      },
    ],
    [selectedItems, getPaymentStatus]
  );

  // Child columns for expanded row
  const renderChildTable = useCallback(
    (parent) => {
      const items = parent?.items || [];
      const allSelected =
        items.length > 0 && items.every((c) => selectedItems[c?.id]);
      const someSelected = items.some((c) => selectedItems[c?.id]);

      const childCols = [
        {
          title: (
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected && !allSelected}
              onChange={(e) => onSelectAllInParent(parent, e.target.checked)}
            />
          ),
          width: 40,
          align: "center",
          render: (_, c) => (
            <Checkbox
              checked={!!selectedItems[c?.id]}
              onChange={(e) => onSelectItem(c?.id, e.target.checked, c)}
            />
          ),
        },
        // Show "Existing" badge in edit mode for items already in voucher
        mode === "put" && {
          title: "Type",
          width: 70,
          align: "center",
          render: (_, c) =>
            c.isExistingItem ? (
              <Tag color="blue" style={{ margin: 0 }}>
                Existing
              </Tag>
            ) : (
              <Tag color="green" style={{ margin: 0 }}>
                New
              </Tag>
            ),
        },
        {
          title: "Item Name",
          dataIndex: "name",
          render: (t) => t || "-",
        },
        {
          title: "Description",
          dataIndex: "description",
          render: (t) => t || "-",
        },
        {
          title: "Amount",
          dataIndex: "total",
          width: 100,
          align: "right",
          render: (v) => (Number(v) || 0).toFixed(2),
        },
        {
          title: "Paid",
          dataIndex: "paid_amount",
          width: 100,
          align: "right",
          render: (v) => (Number(v) || 0).toFixed(2),
        },
        {
          title: "Remaining",
          width: 100,
          align: "right",
          render: (_, c) => {
            const amt = Number(c?.total) || 0;
            const paid = Number(c?.paid_amount) || 0;
                    return (
              <span style={{ color: "#f5222d" }}>
                {(amt - paid).toFixed(2)}
              </span>
            );
          },
        },
        {
          title: "Status",
          width: 80,
          align: "center",
          render: (_, c) => {
            const amt = Number(c?.total) || 0;
            const paid = Number(c?.paid_amount) || 0;
            const status = getPaymentStatus(paid, amt);
            return <Tag color={status.color}>{status.text}</Tag>;
          },
        },
      ].filter(Boolean);

      return (
        <Table
          dataSource={items}
          rowKey="id"
          pagination={false}
          size="small"
          columns={childCols}
        />
      );
    },
    [
      selectedItems,
      onSelectItem,
      onSelectAllInParent,
      getPaymentStatus,
      mode,
    ]
  );

  // Selected items table columns
  const selectedCols = useMemo(
    () =>
      [
        {
          title: "#",
          width: 40,
          align: "center",
          render: (_, __, i) => i + 1,
        },
        // Show "Existing/New" badge in edit mode
        mode === "put" && {
          title: "Type",
          width: 70,
          align: "center",
          render: (_, c) =>
            c.isExistingItem ? (
              <Tag color="blue" style={{ margin: 0 }}>
                Existing
              </Tag>
            ) : (
              <Tag color="green" style={{ margin: 0 }}>
                New
              </Tag>
            ),
          },
          {
          title: "Sale",
          dataIndex: "parentName",
          width: 180,
          render: (t) => <strong>{t}</strong>,
        },
        {
          title: "Item Name",
          dataIndex: "name",
          width: 150,
          render: (t) => t || "-",
        },
        {
          title: "Description",
          dataIndex: "description",
          render: (t) => t || "-",
        },
          {
            title: "Amount",
          width: 100,
          align: "right",
          render: (_, c) => (Number(c?.total) || 0).toFixed(2),
        },
        {
          title: "Pay Amount",
          width: 130,
          align: "center",
          render: (_, c) => (
            <Input
              size="small"
              type="number"
              value={paymentAmounts[c?.id] ?? ""}
              onChange={(e) => onPaymentChange(c?.id, e.target.value, c)}
              style={{ textAlign: "right" }}
            />
          ),
        },
        {
          title: "",
          width: 50,
            align: "center",
          render: (_, c) => (
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => onRemoveSelected(c?.id)}
            />
          ),
        },
      ].filter(Boolean),
    [mode, paymentAmounts, onPaymentChange, onRemoveSelected]
  );

  // Other receipt handlers
  const onOtherChange = (id, field, value) => {
    setOtherItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, [field]: value } : i))
    );
  };

  const addOtherItem = () => {
    const newId = Math.max(...otherItems.map((i) => i.id), 0) + 1;
    setOtherItems((prev) => [
      ...prev,
      { id: newId, account_no: "", description: "", amount: "" },
    ]);
  };

  const removeOtherItem = (id) => {
    if (otherItems.length > 1)
      setOtherItems((prev) => prev.filter((i) => i.id !== id));
  };

  // Submit
  const onSubmit = async (values) => {
    if (isOther) {
      const valid = otherItems.filter(
        (i) => i.account_no && i.description && i.amount
      );
      if (!valid.length)
        return message.warning("Add at least one receipt item");
      if (valid.reduce((s, i) => s + Number(i.amount || 0), 0) <= 0)
        return message.warning("Total must be > 0");
    } else {
      if (!selectedItemsData.length)
        return message.warning("Select at least one item");
      if (total <= 0) return message.warning("Total amount must be > 0");
    }

    setLoading(true);
    try {
      let voucherItems, crTotal;

      if (isOther) {
        const valid = otherItems.filter(
          (i) => i.account_no && i.description && i.amount
        );
        voucherItems = valid.map((i) => ({
          key: i.id,
          account_no: i.account_no,
          description: i.description,
          amount_cr: Number(i.amount),
          amount_dr: 0,
        }));
        crTotal = valid.reduce((s, i) => s + Number(i.amount || 0), 0);
      } else {
        voucherItems = selectedItemsData.map((c) => ({
          key: c.id,
          account_no: selectedAccount,
          description: `${c.name || ""} - ${c.description || ""}`.trim(),
          amount_cr: paymentAmounts[c.id] || 0,
          amount_dr: paymentAmounts[c.id] || 0,  // Same as CR for double-entry balance
          sales_id: c.sales_id,
        }));
        crTotal = total;
      }

      const payload = {
        voucher_date: values.voucher_date,
        voucher_type: page_mode,
        account_no: values.account_no ?? null,
        description: values.description ?? "",
        userid: Number(userId),
        cr_total: crTotal,
        dr_total: crTotal,  // Same as CR for double-entry balance
        totalpaidd: mode === "put" ? crTotal : 0,
        voucherItems,
      };

      const url =
        mode === "post"
          ? `${API.VOUCHER_ACTIONS_RECEIPT}${page_mode}`
          : `${API.VOUCHER_ACTIONS_RECEIPT}${item.id}`;

      if (mode === "post") {
        await POST(url, payload);
      } else {
        await PUT(url, payload);
      }
      message.success("Success");
      onchange();
      onClose();
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getAccName = (no) => {
    const acc = accounts.find((a) => a.account_no === no);
    return acc ? `${acc.account_no} - ${acc.account_name}` : no || "-";
  };

  return (
      <Modal
      title={columnsVisible?.page}
      open={visible}
      onCancel={onClose}
      width={1100}
      footer={null}
        centered
      >
        <Form
          form={form}
        onFinish={onSubmit}
        initialValues={{
          voucher_date:
            mode === "put" && item?.voucher_date
              ? moment(item.voucher_date)
              : moment(),
          description: item?.description,
          account_no: item?.account_no,
          to_account: item?.voucherDetails?.[0]?.account_no,
          receipt_type: receiptType === "sales" ? "Sales Receipt" : "Other Receipt",
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            {isSales && (
                <Form.Item
                  name="to_account"
                  label="Account"
                labelCol={{ span: 6 }}
                rules={[{ required: true }]}
                >
                  <Select
                    size="small"
                    showSearch
                    allowClear
                  placeholder="Select account"
                  disabled={mode === "put"}
                  onChange={(val) => setSelectedAccount(val)}
                  filterOption={(i, o) =>
                    (o?.children?.toString() || "")
                      .toLowerCase()
                      .includes(i.toLowerCase())
                  }
                >
                  {accounts.map((a) => (
                    <Select.Option key={a.account_no} value={a.account_no}>
                      {a.account_no} - {a.account_name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              )}

                <Form.Item
                  name="account_no"
                  label="Supplier"
              labelCol={{ span: 6 }}
              rules={[{ required: true }]}
                >
                  <Select
                    size="small"
                    showSearch
                    allowClear
                placeholder="Select supplier"
                disabled={mode === "put"}
                onChange={(val) => setSelectedSupplier(val)}
                filterOption={(i, o) =>
                  (o?.children?.toString() || "")
                    .toLowerCase()
                    .includes(i.toLowerCase())
                }
              >
                {accounts.map((a) => (
                  <Select.Option key={a.account_no} value={a.account_no}>
                    {a.account_no} - {a.account_name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>

              <Form.Item
                name="receipt_type"
              label="Type"
              labelCol={{ span: 6 }}
              rules={[{ required: true }]}
              >
                <Select
                  size="small"
                disabled={mode === "put"}
                onChange={(val) =>
                  setReceiptType(val === "Sales Receipt" ? "sales" : "other")
                }
                >
                  <Select.Option value="Sales Receipt">
                    Sales Receipt
                  </Select.Option>
                  <Select.Option value="Other Receipt">
                    Other Receipt
                  </Select.Option>
                </Select>
              </Form.Item>
            </Col>

          <Col span={12}>
              <Form.Item
                name="voucher_date"
              label="Date"
              labelCol={{ span: 6 }}
              rules={[{ required: true }]}
              >
                <DatePicker
                  size="small"
                format="DD/MM/YYYY"
                  style={{ width: "100%" }}
                />
              </Form.Item>
              <Form.Item
                name="description"
                label="Description"
              labelCol={{ span: 6 }}
              rules={[{ required: true, message: "Description is required" }]}
            >
              <Input.TextArea rows={2} size="small" />
              </Form.Item>
            </Col>
          </Row>

        <div style={{ marginTop: 16 }}>
          {isOther ? (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 8,
                }}
              >
                <strong>Receipt Items</strong>
                <Button
                  size="small"
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={addOtherItem}
                >
                  Add
                </Button>
              </div>
          <Table
                dataSource={otherItems}
                rowKey="id"
            pagination={false}
            size="small"
                columns={[
                  { title: "#", width: 40, render: (_, __, i) => i + 1 },
                  {
                    title: "Account",
                    width: 300,
                    render: (_, r) => (
                      <Select
                        size="small"
                        style={{ width: "100%" }}
                        value={r.account_no || undefined}
                        onChange={(v) => onOtherChange(r.id, "account_no", v)}
                        showSearch
                        allowClear
                        filterOption={(i, o) =>
                          (o?.children?.toString() || "")
                            .toLowerCase()
                            .includes(i.toLowerCase())
                        }
                      >
                        {accounts.map((a) => (
                          <Select.Option
                            key={a.account_no}
                            value={a.account_no}
                          >
                            {a.account_no} - {a.account_name}
                          </Select.Option>
                        ))}
                      </Select>
                    ),
                  },
                  {
                    title: "Description",
                    render: (_, r) => (
                      <Input
                        size="small"
                        value={r.description}
                        onChange={(e) =>
                          onOtherChange(r.id, "description", e.target.value)
                        }
                      />
                    ),
                  },
                  {
                    title: "Amount",
                    width: 120,
                    render: (_, r) => (
                      <Input
                        size="small"
                        type="number"
                        value={r.amount}
                        onChange={(e) =>
                          onOtherChange(r.id, "amount", e.target.value)
                        }
                      />
                    ),
                  },
                  {
                    title: "",
                    width: 50,
                    render: (_, r) => (
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => removeOtherItem(r.id)}
                        disabled={otherItems.length === 1}
                      />
                    ),
                  },
                ]}
                summary={() => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell colSpan={3}>
                      <strong>Total</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell>
                      <strong>
                        {otherItems
                          .reduce((s, i) => s + Number(i.amount || 0), 0)
                          .toFixed(2)}
                      </strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell />
                  </Table.Summary.Row>
                )}
              />
            </>
          ) : (
            <>
              {/* Sales Selection Table */}
              <div
                style={{
                  marginBottom: 12,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 8,
                }}
              >
                <Space>
                  <strong>Select Sales</strong>
                  {mode === "put" && (
                    <Checkbox
                      checked={showNewItems}
                      onChange={(e) => setShowNewItems(e.target.checked)}
                    >
                      <span style={{ color: "#1890ff" }}>Add New Items</span>
                    </Checkbox>
                  )}
                </Space>
                <Space wrap>
                  <Input
                    placeholder="Search sale, item, description..."
                    prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    allowClear
                    style={{ width: 250 }}
                    size="small"
                  />
                  <RangePicker
                    size="small"
                    format="DD/MM/YYYY"
                    value={dateRange}
                    onChange={setDateRange}
                    placeholder={["From Date", "To Date"]}
                    allowClear
                    style={{ width: 220 }}
                  />
                </Space>
              </div>
              <Table
                dataSource={parentData}
                columns={parentCols}
            rowKey="key"
                pagination={false}
                size="small"
            loading={tableLoading}
                expandable={{
                  expandedRowRender: renderChildTable,
                  expandedRowKeys: expandedKeys,
                  onExpandedRowsChange: setExpandedKeys,
                  expandIcon: ({ expanded, onExpand, record }) => (
                    <Button
                      type="text"
                      size="small"
                      icon={expanded ? <DownOutlined /> : <RightOutlined />}
                      onClick={(e) => onExpand(record, e)}
                    />
                  ),
                }}
                locale={{
                  emptyText: !selectedSupplier
                    ? "Select a supplier"
                    : searchText || dateRange
                    ? "No items match the filters"
                    : "No pending items",
                }}
              />

              {/* Selected Items Table */}
              {selectedItemsData.length > 0 && (
                <>
                  <Divider style={{ margin: "16px 0" }} />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 8,
                    }}
                  >
                    <strong>Selected Items ({selectedItemsData.length})</strong>
                    <span>
                      Total:{" "}
                      <strong style={{ color: "#1890ff", fontSize: 16 }}>
                        {total.toFixed(2)}
                      </strong>
                    </span>
                  </div>
                  <Table
                    dataSource={selectedItemsData}
                    columns={selectedCols}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    bordered
                    summary={() => (
                      <Table.Summary.Row>
                        <Table.Summary.Cell
                          colSpan={5 + (mode === "put" ? 1 : 0)}
                        >
                          <strong>Total Amount</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell align="center">
                          <strong style={{ color: "#1890ff" }}>
                            {total.toFixed(2)}
                          </strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell />
                      </Table.Summary.Row>
                    )}
                  />
                </>
              )}
            </>
          )}
        </div>

        <Row justify="end" style={{ marginTop: 16 }} gutter={8}>
          <Col>
              <ReactToPrint
                trigger={() => (
                <Button icon={<PrinterOutlined />}>Print</Button>
                )}
                content={() => printRef.current}
              />
            </Col>
          <Col>
            <Button onClick={onClose}>Cancel</Button>
            </Col>
          <Col>
            <Button type="primary" htmlType="submit" loading={loading}>
              {mode === "post" ? "Save" : "Update"}
              </Button>
            </Col>
          </Row>
        </Form>

      {/* Print */}
      <div style={{ display: "none" }}>
        <div ref={printRef} style={{ padding: 20 }}>
          <h2 style={{ textAlign: "center" }}>{columnsVisible?.page}</h2>
          <p>
                  <strong>Date:</strong>{" "}
            {form.getFieldValue("voucher_date")?.format("DD/MM/YYYY")}
          </p>
          <p>
            <strong>Supplier:</strong>{" "}
            {getAccName(form.getFieldValue("account_no"))}
          </p>
          {isSales && (
            <p>
              <strong>Account:</strong>{" "}
              {getAccName(form.getFieldValue("to_account"))}
            </p>
          )}
          <p>
            <strong>Description:</strong> {form.getFieldValue("description")}
          </p>
          <Table
            size="small"
            pagination={false}
            bordered
            rowKey="id"
            dataSource={
              isOther
                ? otherItems.filter((i) => i.account_no && i.amount)
                : selectedItemsData
            }
            columns={
              isOther
                ? [
                    { title: "#", render: (_, __, i) => i + 1 },
                    {
                      title: "Account",
                      dataIndex: "account_no",
                      render: getAccName,
                    },
                    { title: "Description", dataIndex: "description" },
                    {
                      title: "Amount",
                      dataIndex: "amount",
                      align: "right",
                      render: (v) => Number(v || 0).toFixed(2),
                    },
                  ]
                : [
                    { title: "#", render: (_, __, i) => i + 1 },
                    { title: "Sale", dataIndex: "parentName" },
                    { title: "Item Name", dataIndex: "name" },
                    { title: "Description", dataIndex: "description" },
                    {
                      title: "Pay Amount",
                      align: "right",
                      render: (_, c) =>
                        (paymentAmounts[c.id] || 0).toFixed(2),
                    },
                  ]
            }
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={isOther ? 3 : 4}>
                  <strong>Total</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  <strong>
                    {(isOther
                      ? otherItems
                          .filter((i) => i.account_no && i.amount)
                          .reduce((s, i) => s + Number(i.amount || 0), 0)
                      : total
                    ).toFixed(2)}
                  </strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
          </div>
        </div>
    </Modal>
  );
};

export default FormModalReceipt;
// Also export with original typo name for backward compatibility
export { FormModalReceipt as FormModalReciept };
